import { GraphModel } from '../graph/graph.model';
import { DocumentModel } from './document.model';
import { DG } from '@cotera/sdk/core';
import { z } from 'zod/v3';
import { err, ok } from 'neverthrow';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '../actions/framework';
import { Assert, assertExists } from '@cotera/utilities';
import { toast } from '@cotera/client/app/components/ui/toaster';
import { GRAPH_ACTION_GROUP } from '../dataset/actions/constants';
import { AskResponse } from '../actions/framework/types';
import { useState } from 'react';
import { Text, Loading } from '@cotera/client/app/components/ui';
import { Input } from '@cotera/client/v0/components/ui/input';
import { Label } from '@cotera/client/v0/components/ui/label';
import { Button } from '@cotera/client/v0/components/ui/button';
import { ModalPageView } from '@cotera/client/app/components/app';

type Params = {};

export class DeleteDocumentAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Delete Document';
  description = 'Deletes the document from the graph';
  inputSchema = z.object({});
  group = GRAPH_ACTION_GROUP;
  icon = 'trash';

  constructor(
    private readonly navigator: {
      close: (id: string) => void;
    },
    private readonly modalManager: {
      push: (props: {
        view: () => React.ReactNode;
        onClose?: () => void;
      }) => void;
      pop: () => void;
      close: () => void;
    }
  ) {
    super();
  }

  /**
   * There needs to be only 1 document in context
   *
   * The document needs to have been "saved" at least once.
   */
  applicable(context: ApplicableContext): boolean {
    if (!context.hasTargetedModelOfType(DocumentModel)) {
      return false;
    }

    const document = assertExists(context.targetedModelOfType(DocumentModel));

    return document.documentIdentity.snapshot() !== null;
  }

  async execute(
    _: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const document = context.targetedModelOfType(DocumentModel);
    const graph = context.getInScopeModelOfType(GraphModel);

    Assert.assert(document !== null, 'Document not found');
    Assert.assert(graph !== null, 'Graph not found');

    const documentName = document.currentNameInGraph.snapshot();
    Assert.assert(documentName !== null); // by applicable

    const deleteOp = DG.Operations.delete({
      symbol: documentName,
    });

    const deleteOpResult = await graph.attemptToStageOperations([deleteOp]);

    if (deleteOpResult.isErr()) {
      toast.error(JSON.stringify(deleteOpResult.error.err));
      return err([]);
    }

    this.navigator.close(`/document/${documentName}`);

    return ok({});
  }

  shouldAsk(_: ApplicableContext): boolean {
    return true;
  }

  ask(_: {}, context: ExecuteContext): AskResponse<Params> {
    const document = context.targetedModelOfType(DocumentModel);
    Assert.assert(document !== null, 'Document not found');
    const documentName = Assert.assertExists(
      document.currentNameInGraph.snapshot()
    );

    return {
      view: ({ onRespond, onCancel }) => {
        this.modalManager.push({
          view: () => {
            return (
              <AskView
                onRespond={async (params) => {
                  await onRespond(params);
                  this.modalManager.pop();
                }}
                documentName={documentName}
              />
            );
          },
          onClose: async () => {
            await onCancel();
          },
        });
      },
    };
  }
}

export const AskView: React.FC<{
  onRespond: (params: Params) => Promise<void>;
  documentName: string;
}> = ({ onRespond, documentName }) => {
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  return (
    <ModalPageView
      title={`Delete ${documentName}`}
      subtitle={'Are you sure you want to delete this document?'}
      className="px-4 py-4"
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={async (e) => {
          e.preventDefault();
        }}
      >
        <div className="flex flex-col gap-2">
          <Label htmlFor="document-name-confirm" className="text-sm">
            Please type the document name
          </Label>
          <Input
            id="document-name-confirm"
            autoFocus
            placeholder={documentName}
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="aria-invalid:border-destructive"
            aria-invalid={error ? true : undefined}
          />
        </div>
        {error && (
          <Text.Caption className="text-destructive">{error}</Text.Caption>
        )}
        <div className="w-full flex justify-end">
          <Button
            type="submit"
            disabled={loading || name !== documentName}
            variant="destructive"
            icon="save"
            className="w-fit self-end"
            onClick={async () => {
              if (name === documentName) {
                setLoading(true);
                await onRespond({});
                setLoading(false);
              } else {
                setError('Incorrect document name');
              }
            }}
          >
            {loading ? <Loading.Spinner variant="sm" /> : 'Delete'}
          </Button>
        </div>
      </form>
    </ModalPageView>
  );
};
